import * as Yup from "yup";

import {
  UpdateReinsurerExtraCharges,
  UpdateReinsurerExtraChargesVariables,
} from "_graphql/mutation/__generated__/UpdateReinsurerExtraCharges";

import { FacultativeDeductionForm } from "components/forms";
import { LocationGenerics } from "router/location";
import { Modal } from "components";
import { UPDATE_EXTRA_CHARGE } from "_graphql/visal/mutations/re-insurer";
import toast from "react-hot-toast";
import { useAppLocation } from "_graphql/cache/auth";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { useOffer } from "hooks/data/use-offers";
import { useSearch } from "react-location";
import { wrapClick } from "utils";

export const insurerRepValidationSchema = Yup.object().shape({
  agreed_brokerage_percentage: Yup.number()
    .min(0, "Agreed Brokerage Percentage must be at least 0")
    .max(100, "Agreed Brokerage Percentage cannot exceed 100")
    .required("Agreed Brokerage Percentage is required"),
  agreed_commission: Yup.number()
    .min(0, "Agreed Commission must be at least 0")
    .max(100, "Agreed Commission cannot exceed 100")
    .required("Agreed Commission is required"),
  nic_levy: Yup.number()
    .min(0, "NIC Levy must be at least 0")
    .max(100, "NIC Levy cannot exceed 100")
    .required("NIC Levy is required"),
  withholding_tax: Yup.number()
    .min(0, "Withholding Tax must be at least 0")
    .max(100, "Withholding Tax cannot exceed 100")
    .required("Withholding Tax is required"),
});

export default function SetupDeductionContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const { params } = useAppLocation()
  const { offer, loading: fetching } = useOffer({
    offer_id: searchParams?.id || "",
  });

  const [create, { loading }] = useMutation<
    UpdateReinsurerExtraCharges,
    UpdateReinsurerExtraChargesVariables
  >(UPDATE_EXTRA_CHARGE);

  const form = useFormik<any>({
    initialValues: {
      agreed_brokerage_percentage: "",
      agreed_commission: "",
      nic_levy: "",
      withholding_tax: "",
    },
    validationSchema: insurerRepValidationSchema,
    onSubmit: async (variables) => {
      await create({
        variables: {
          extraCharge: {
            reinsurer_data: {
              agreed_brokerage_percentage:
                variables.agreed_brokerage_percentage,
              agreed_commission: variables.agreed_commission,
              nic_levy: variables.nic_levy,
              withholding_tax: variables.withholding_tax,
              reinsurer_id: searchParams?.reinsurer || "",
              reinsurer_participant_id: searchParams?.reinsurer || "",
            },
            offer_id: offer?.offer_id,
          },
          extraChargeId:
            offer?.reinsurer_offer_extra_charge?.offer_extra_charge_id || "",
          participantId: params?.reInsurer || "",
        },
      }).then(({ data }) => {
        if (data?.updateReinsurerExtraCharges) {
          toast.success("Re-Insurer Representative updated successfully");
          refetch?.();
          form.resetForm();
        } else {
          toast.error("Failed to update Re-Insurer Representative");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (offer) {
      form.setValues({
        agreed_brokerage_percentage:
          offer?.reinsurer_offer_extra_charge?.agreed_brokerage_percentage ||
          "",
        agreed_commission:
          offer?.reinsurer_offer_extra_charge?.agreed_commission || "",
        nic_levy: offer?.reinsurer_offer_extra_charge?.nic_levy || "",
        withholding_tax:
          offer?.reinsurer_offer_extra_charge?.withholding_tax || "",
      });
    }
  }, [offer]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={fetching}
      title="Update Deduction Charges"
      description="Update the deduction charges for this re-insurer."
      renderActions={() => (
        <>
          <button
            type="button"
            disabled={loading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Updating Deduction..." : "Update Deduction"}
          </button>
        </>
      )}
    >
      {/* {JSON.stringify(form.errors, null, 2)} */}
      <FacultativeDeductionForm form={form} program={offer} />
    </Modal>
  );
}
